import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { DEFAULT_DATE_FORMAT, Module } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { getFirstDayOfMonth } from "@/lib/date";

import {
  displayLimitWithDepartment,
  displayProjectLimitWithRole,
  readProjectLimitWithDepartment,
  readProjectLimitWithRole,
} from "@/schemas/projectLimit";
import { SuccessFetchListResponse } from "@/services/api";
import {
  ReadProjectLimit,
  ReadProjectLimitWithDepartment,
  ReadProjectLimitWithRole,
} from "@/types/projectLimit";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { formatDate } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { ProjectService } from "../../service/project";
import { ProjectLimitService } from "../../service/projectLimit";
import ProjectLimitEditor from "../limits/ProjectLimitEditor";
import ProjectLimitSummary from "../limits/ProjectLimitSummary";
import { ProjectLimitsContextProvider } from "../limits/ProjectLimitsContext";
import Spinner from "@/components/Spinner";
import { isProjectRoleDependent } from "../../lib/isProjectRoleDependent";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";

const standardInitialColumnVisibility = {
  // Hide all columns by default
  ...Object.fromEntries(
    Object.keys(displayLimitWithDepartment.unwrap().shape).map((key) => [
      key,
      false,
    ]),
  ),
  // Show the following columns by default
  ref_number: true,
  client_store_number: true,
  name: true,
  hours: true,
  rate: true,
  department_name: true,
  addition: true,
};

const dedicatedInitialColumnVisibility = {
  // Hide all columns by default
  ...Object.fromEntries(
    Object.keys(displayProjectLimitWithRole.unwrap().shape).map((key) => [
      key,
      false,
    ]),
  ),
  // Show the following columns by default
  ref_number: true,
  client_store_number: true,
  name: true,
  hours: true,
  rate: true,
  project_role: true,
  addition: true,
};

const ProjectLimit = <P extends ReadProjectLimit>() => {
  const projectId = useProjectId();

  const projectQuery = useQuery({
    queryKey: [Module.ProjectLimits, projectId],
    queryFn: () => ProjectService.get(projectId),
  });

  const isRoleDependent = isProjectRoleDependent(projectQuery.data);

  const name = isRoleDependent
    ? "projectLimitWithRoles"
    : "projectLimitWithDepartments";

  const schema = isRoleDependent
    ? readProjectLimitWithRole
    : readProjectLimitWithDepartment;

  const initialColumnVisibility = isRoleDependent
    ? dedicatedInitialColumnVisibility
    : standardInitialColumnVisibility;

  const getItemId = useCallback(
    (item: P) => {
      if (isRoleDependent) {
        return (item as ReadProjectLimitWithRole).project_role_id;
      }
      return (item as ReadProjectLimitWithDepartment).department_id;
    },
    [isRoleDependent],
  );

  const [selectedMonthYear, setSelectedMonthYear] = useState(
    getFirstDayOfMonth(new Date()),
  );
  const dateString = formatDate(selectedMonthYear, DEFAULT_DATE_FORMAT);
  const options = useCreateDataTableOptions<ReadProjectLimit>(name);

  const queryKey = useDynamicQueryParams([name, projectId], options, {
    dateString,
  });

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      ProjectLimitService.getList(projectId, dateString, {
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [projectId, dateString, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadProjectLimitWithDepartment>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  const onMonthChange = useCallback(
    (date: Date) => {
      setSelectedMonthYear(date);
    },
    [setSelectedMonthYear],
  );

  return (
    <div className="flex flex-col">
      <DataTableQueryContextProvider value={queryValue}>
        <ProjectLimitsContextProvider
          project={projectQuery.data}
          getItemId={getItemId as (item: unknown) => string}
          period={selectedMonthYear}
        >
          {projectQuery.isLoading ? (
            // We need to display spinner instead of project limit data
            // because we don't know yet if the project is role or department dependent
            // and we need to know that before we can render any columns
            <Spinner className="h-screen w-screen" size="lg" />
          ) : (
            <>
              <ProjectLimitEditor
                name={name}
                projectQuery={projectQuery}
                projectId={projectId}
                schema={schema}
                initialColumnVisibility={initialColumnVisibility}
                selectedMonthYear={selectedMonthYear}
                onMonthChange={onMonthChange}
                options={options}
              />
              <ProjectLimitSummary />
            </>
          )}
        </ProjectLimitsContextProvider>
      </DataTableQueryContextProvider>
    </div>
  );
};

export default ProjectLimit;
